import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import {
  BookingRowsView,
  FareSummaryView,
  JsonPanel,
  DiscountControls,
  UserDropdown,
  show,
  useNotificationPolling,
  userLabel,
} from "./createBookingShared";
import CreateRoundTripFromTbo from "./CreateRoundTripFromTbo";
import CreateMultiCityFromTbo from "./CreateMultiCityFromTbo";

const TRIP_MODES = [
  { value: "oneway", label: "One-way / Combined round trip", hint: "One PNR (current flow)" },
  { value: "roundtrip", label: "Round trip (2 PNRs)", hint: "Onward and return booked as separate PNRs" },
  { value: "multicity", label: "Multi-city", hint: "One PNR with 2 or more trips" },
];

export default function CreateBookingFromTbo() {
  const navigate = useNavigate();
  const [tripMode, setTripMode] = useState("oneway");
  const [pnr, setPnr] = useState("");
  const [tboBookingId, setTboBookingId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [allowUnticketed, setAllowUnticketed] = useState(false);
  // The convenience fee is always applied; there is no toggle.
  const showFee = true;
  const [paymentId, setPaymentId] = useState("");
  // Calculate Fee choices; the server recalculates the fare with them.
  const [promoCode, setPromoCode] = useState("");
  const [redeem, setRedeem] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [recalculating, setRecalculating] = useState(false);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  useNotificationPolling(created?.booking_id, created?.notifications?.status, ({ status, notifications }) =>
    setCreated((c) => (c ? { ...c, notifications: { status, ...(notifications || {}) } } : c))
  );

  // TBO looks a booking up by PNR + BookingId, or PNR + passenger first/last name.
  const hasLookup = tboBookingId.trim() || (firstName.trim() && lastName.trim());
  const canSubmit = pnr.trim() && hasLookup && user && !loading;

  const lookupBody = () => ({
    pnr: pnr.trim(),
    tbo_booking_id: tboBookingId.trim() || undefined,
    first_name: firstName.trim() || undefined,
    last_name: lastName.trim() || undefined,
    user_id: user.user_id,
  });

  const fetchDetails = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setErr("");
    setResult(null);
    setCreated(null);
    setAllowUnticketed(false);
    setPaymentId("");
    setPromoCode("");
    setRedeem("");
    setRedeemAmount("");
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/preview", lookupBody());
      setResult(res.data?.data || null);
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2.message || "Failed to fetch booking from TBO";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Re-runs the preview with the chosen promo / redeem so the fee is
  // recalculated server-side exactly as confirm will save it.
  const recalculateFee = async (nextPromo, nextRedeem, nextAmount = "") => {
    setPromoCode(nextPromo);
    setRedeem(nextRedeem);
    setRedeemAmount(nextAmount);
    setRecalculating(true);
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/preview", {
        ...lookupBody(),
        promo_code: nextPromo || undefined,
        redeem: nextRedeem || undefined,
        redeem_amount: nextRedeem && nextAmount ? Number(nextAmount) : undefined,
      });
      setResult(res.data?.data || null);
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Fee recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  const itinerary = result?.actual_response?.Response?.FlightItinerary;
  const ticketing = result?.ticketing;
  // Ticking Calculate Fee switches to the rows with the checkout fee applied,
  // which is also what confirm will save (the server recalculates it).
  const rows = showFee && result?.converted_response_with_fee ? result.converted_response_with_fee : result?.converted_response;
  const b = rows?.booking;
  const notFullyTicketed = ticketing && ticketing.ticketed < ticketing.total;
  const canCreate =
    rows &&
    !result.existing_booking &&
    !created &&
    !creating &&
    !recalculating &&
    ticketing?.total > 0 &&
    (!notFullyTicketed || allowUnticketed);

  const createBooking = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const res = await api.post("/admin/bookings/create-from-tbo/confirm", {
        pnr: pnr.trim(),
        tbo_booking_id: tboBookingId.trim() || undefined,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        user_id: user.user_id,
        trace_id: b?.trace_id,
        allow_unticketed: notFullyTicketed ? allowUnticketed : undefined,
        apply_convenience_fee: showFee && !!result?.fare_summary,
        promo_code: showFee ? promoCode || undefined : undefined,
        redeem: showFee ? redeem || undefined : undefined,
        redeem_amount: showFee && redeem && redeemAmount ? Number(redeemAmount) : undefined,
        payment_id: paymentId.trim() || undefined,
      });
      setCreated(res.data?.data || {});
      toast.success("Booking created");
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Failed to create booking");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/admin/bookings")}
        className="text-sm text-gray-600 hover:text-gray-900 font-semibold mb-4"
      >
        ← Back to Bookings
      </button>

      <h1 className="text-2xl font-bold">Create Booking</h1>

      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Trip type">
        {TRIP_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={tripMode === m.value}
            onClick={() => setTripMode(m.value)}
            className={`rounded-xl border px-4 py-2 text-left text-sm ${
              tripMode === m.value ? "border-black bg-black text-white" : "hover:bg-gray-50"
            }`}
          >
            <div className="font-semibold">{m.label}</div>
            <div className={`text-xs ${tripMode === m.value ? "text-gray-300" : "text-gray-500"}`}>{m.hint}</div>
          </button>
        ))}
      </div>

      {tripMode === "roundtrip" ? (
        <CreateRoundTripFromTbo />
      ) : tripMode === "multicity" ? (
        <CreateMultiCityFromTbo />
      ) : (
      <>

      <form onSubmit={fetchDetails} className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">PNR</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 uppercase outline-none focus:ring-2 focus:ring-black"
            placeholder="e.g. 9EQ9QM"
            value={pnr}
            onChange={(e) => setPnr(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">Booking ID</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="TBO BookingId e.g. 2194049"
            inputMode="numeric"
            value={tboBookingId}
            onChange={(e) => setTboBookingId(e.target.value.replace(/\D/g, ""))}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">First Name</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="If no Booking ID"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">Last Name</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="If no Booking ID"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[320px]">
          <label className="text-sm font-semibold">User</label>
          <UserDropdown value={user} onChange={setUser} />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-xl bg-black text-white px-5 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? "Fetching..." : "Get Booking Details"}
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      {result && (
        <div className="mt-6 space-y-4">
          <div className="border rounded-2xl p-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-gray-500">PNR</div>
              <div className="font-semibold">{itinerary?.PNR || "-"}</div>
            </div>
            <div>
              <div className="text-gray-500">TBO Booking ID</div>
              <div className="font-semibold">{itinerary?.BookingId || "-"}</div>
            </div>
            <div>
              <div className="text-gray-500">Route</div>
              <div className="font-semibold">
                {itinerary?.Origin || "-"} → {itinerary?.Destination || "-"} ({itinerary?.AirlineCode || "-"})
              </div>
            </div>
            <div>
              <div className="text-gray-500">User</div>
              <div className="font-semibold truncate">{result.user ? userLabel(result.user) : "-"}</div>
            </div>
          </div>

          {ticketing && ticketing.ticketed < ticketing.total && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Not fully ticketed: {ticketing.ticketed}/{ticketing.total} passengers have a ticket number.
            </div>
          )}
          {result.existing_booking && (
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              A booking already exists for this PNR/BookingId (booking #{result.existing_booking.booking_id},{" "}
              {result.existing_booking.booking_ticket_number || "no ticket ref"}).
              {result.warning ? ` ${result.warning}` : ""}
            </div>
          )}
          {result.convert_error && (
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              Conversion failed: {result.convert_error}
            </div>
          )}

          {b && (
            <>
              <BookingRowsView rows={rows} paymentIdOverride={paymentId.trim()} />
            </>
          )}

          <div className="border rounded-2xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">Fare Summary</div>
            {showFee && (
              <div className="p-4">
                {result.fare_summary_error ? (
                  <div className="text-sm text-red-600">Fee calculation failed: {result.fare_summary_error}</div>
                ) : !result.fare_summary ? (
                  <div className="text-sm text-gray-500">No fare summary available.</div>
                ) : (
                  <div className="space-y-4">
                    <DiscountControls
                      result={result}
                      promoCode={promoCode}
                      redeem={redeem}
                      redeemAmount={redeemAmount}
                      disabled={!!created || recalculating}
                      onChange={recalculateFee}
                    />
                    {recalculating && <div className="text-sm text-gray-500">Recalculating fee...</div>}
                    {Number(result.fare_summary.navcash_cashback_amount) > 0 && (
                      <div className="rounded-xl border border-green-300 bg-green-50 px-4 py-2 text-sm text-green-800">
                        Promo {result.fare_summary.promo_code} applied as NavCash cashback of{" "}
                        <b>{show(result.fare_summary.navcash_cashback_amount)}</b>, credited to the wallet after travel. It
                        does not reduce the Total Payable.
                      </div>
                    )}
                    {(result.discount_warnings || []).map((w) => (
                      <div key={w} className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                        {w}
                      </div>
                    ))}
                    <FareSummaryView summary={result.fare_summary} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {created ? (
              <div className="text-sm space-y-1">
                <div className="text-green-700">
                  Booking created: <b>{created.booking_ticket_number || `#${created.booking_id}`}</b> (booking_id{" "}
                  {created.booking_id}, trace_id {created.trace_id}, total {show(created.totalfare)}, convenience fee{" "}
                  {show(created.convenience_fee)}, NavCash earn {show(created.navcash_earn)} credited to pending wallet).
                </div>
                <div className="text-gray-700">
                  Payment ID: <b>{show(created.payment_id)}</b>
                  {created.payment_warning ? <span className="text-amber-700"> · {created.payment_warning}</span> : null}
                </div>
                {created.notifications?.status === "running" ? (
                  <div className="text-gray-600">Sending push notification, ticket email and invoice...</div>
                ) : created.notifications?.status === "timeout" ? (
                  <div className="text-amber-700">
                    Still sending in the background. Check the booking later for the ticket and invoice.
                  </div>
                ) : created.notifications ? (
                  <div className="text-gray-700">
                    Push: <b>{show(created.notifications.push)}</b> · Ticket email:{" "}
                    <b>{show(created.notifications.ticket_email)}</b> · Invoice: <b>{show(created.notifications.invoice)}</b>
                    {created.notifications.error ? ` · Error: ${created.notifications.error}` : ""}
                  </div>
                ) : null}
              </div>
            ) : notFullyTicketed ? (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowUnticketed} onChange={(e) => setAllowUnticketed(e.target.checked)} />
                Create anyway, even though not every passenger is ticketed
              </label>
            ) : (
              <div className="text-sm text-gray-600">Review the details above, then create the booking.</div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              {!created && (
                <input
                  className="border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black sm:w-64"
                  placeholder="Payment ID (optional, pay_...)"
                  value={paymentId}
                  onChange={(e) => setPaymentId(e.target.value.trim())}
                />
              )}
              {created?.trace_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/bookings/edit/trace/${created.trace_id}`)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Open Booking
                </button>
              )}
              {!created && (
                <button
                  type="button"
                  onClick={createBooking}
                  disabled={!canCreate}
                  className="rounded-xl bg-black text-white px-5 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {creating ? "Creating & sending..." : "Create Booking"}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <JsonPanel title="Actual Booking Response (TBO)" data={result.actual_response} />
            <JsonPanel
              title={showFee && result.converted_response_with_fee ? "Converted Booking Response (with fee)" : "Converted Booking Response"}
              data={rows}
            />
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
